<template>
  <Html :lang="$i18n.locale">
    <Body
      id="app"
      :data-page="pageName"
      :class="{ 'is-dev-domain': isDevDomain }"
    >
      <el-config-provider :locale="elLocale">
        <NuxtLoadingIndicator
          color="#f9f7f7"
          :height="1"
        />
        <NuxtLayout>
          <NuxtPage />
        </NuxtLayout>

        <LazyCapacitorDeepLink v-if="useRuntimeConfig().public.isIOSApp" />
        <LazyCapacitorAppUpdate v-if="useRuntimeConfig().public.isIOSApp" />
        <LazyCapacitorOtaUpdate v-if="useRuntimeConfig().public.isIOSApp" />
      </el-config-provider>
    </Body>
  </Html>
</template>

<script setup>
import ua from 'element-plus/dist/locale/uk.mjs';
import cs from 'element-plus/dist/locale/cs.mjs';

const i18n = useNuxtApp().$i18n;

// Computed
const elLocale = computed(() => {
  if (i18n.locale.value === 'ua') {
    return _merge(ua, {});
  }
  return _merge(cs, {
    el: {
      datepicker: {
        year: '',
      },
    },
  });
});
const pageName = computed(() => useRoute().name?.split?.('___')?.[0]);
const isDevDomain = computed(() => {
  if (!import.meta.client) return false;
  const hostname = window.location.hostname.toLowerCase();
  return hostname === 'tichalinka.dev' || hostname.endsWith('.tichalinka.dev');
});

useHead({
  titleTemplate: (titleChunk) => titleChunk ? `${titleChunk} | Tichá linka` : 'Tichá linka',
});

// Watchers
watch(i18n.locale, () => {
  dayjs.locale(i18n.locale.value === 'ua' ? 'uk' : 'cs');
  useNuxtApp()?.$sentry?.setTag?.('locale', i18n.locale.value);
}, { immediate: true });

// Watchers - Loggers
watch(() => useAuthStore().user, (user) => {
  if (!user) return;

  if (useNuxtApp().$sentry) {
    useNuxtApp()?.$sentry?.setUser({
      id: user?.id,
      email: user?.email,
      username: user.name ? user.name : `${user.first_name} ${user.last_name}`,
    });

    useNuxtApp()?.$sentry?.setTag?.('user.is_operator', user?.is_operator);
    useNuxtApp()?.$sentry?.setTag?.('user.type', user?.type);
    useNuxtApp()?.$sentry?.setTag?.('buildDate', useRuntimeConfig().public.buildDate);

    window.getReplayURL = () => `https://tichysvet.sentry.io/replays/${useNuxtApp().$replay?.getReplayId?.()}`;
  }
}, { immediate: true });

// Watchers - Layout reset
watch(() => useRoute().path, () => {
  useUIStore().burgerMenu = false;
}, { immediate: true });

// Feature - isNetworkOnlineWatcher
const isOnline = useOnline();
const notifyBox = ref(null);
const audio = ref(new Audio('/sounds/operator_offline.mp3'));

watch(isOnline, () => {
  console.log('[isOnline]', isOnline.value);

  if (!isOnline.value) {
    if (!document.title.includes('🔴')) {
      document.title = `🔴 ${document.title}`;
    }

    notifyBox.value = ElNotification.error({
      title: 'Nejste připojeni k internetu',
      duration: 0,
      offset: 80,
      position: 'top-right',
      showClose: false,
      customClass: 'el-notification--danger',
      onClose: () => {
        if (document.title.includes('🔴')) {
          document.title = document.title.replace('🔴 ', '');
        }
      },
    });
  } else if (notifyBox.value) {
    notifyBox.value.close();
  }
}, { immediate: true });

// Feature - offline sound (#1562)
// navigator.onLine alone misses server-side outages where the local network stays up,
// so the sound is driven by the websocket connection state too.
const OFFLINE_SOUND_GRACE = ms('5s');
let wsDownSettleTimer = null;

// Websocket loss counts as offline only after a grace period, so short reconnects
// (retry runs every 2s) don't trigger a false alarm; reconnect clears it right away.
// Real network loss (isOnline) stays instantaneous. wasClosed keeps the initial
// page load quiet — before the first connect, "not connected" is not an outage.
// A logged-out session is not an outage either: a 401 logs out without reloading, so
// without isLoggedIn the flag would still be set when the operator signs back in and
// the alarm would sound for the moment before the fresh socket connects.
const wsDownSettled = ref(false);
watch(() => {
  const { isConnected, wasClosed } = useNuxtApp().$store.state.websocket;
  return !isConnected && wasClosed && useAuthStore().isLoggedIn;
}, (isDown) => {
  clearTimeout(wsDownSettleTimer);

  if (isDown) {
    wsDownSettleTimer = setTimeout(() => {
      wsDownSettled.value = true;
    }, OFFLINE_SOUND_GRACE);
  } else {
    wsDownSettled.value = false;
  }
}, { immediate: true });

const isOfflineForOperator = computed(() => (!isOnline.value || wsDownSettled.value) && !!useAuthStore().user?.is_operator);

const playOfflineSound = async() => {
  const isPlayedByExtension = await useUIStore().chromeExtensionSendMessage({ playAudio: 'operator_offline' });
  if (!isPlayedByExtension && audio.value) {
    audio.value.loop = true;
    audio.value.play().catch((error) => {
      console.warn(error);
    });
  }
};

const stopOfflineSound = async() => {
  // Stop through both channels unconditionally: the loop may run in the extension's
  // offscreen document (which survives page reloads) or in the local fallback audio.
  await useUIStore().chromeExtensionSendMessage({ stopAudio: 'operator_offline' });
  if (audio.value && !audio.value.paused) {
    audio.value.pause();
  }
};

watch(isOfflineForOperator, (isOffline) => {
  if (isOffline) {
    playOfflineSound();
  } else {
    // Also runs on app start (immediate) — kills an orphaned offscreen loop
    // left behind by a previous page load.
    stopOfflineSound();
  }
}, { immediate: true });
</script>

<style>
body.is-dev-domain {
  border-top: 6px solid red;
}
</style>
